/**
 * NOS-46: Hooks for events (list, single, create, update, delete).
 * Requires can_manage_events permission (enforced by RLS and UI).
 */

import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import type { Database, Json } from "@/types/database"
import { generateSlug } from "@/lib/slugUtils"
import { queuePublicCachePurge } from "@/lib/publicCache"

type EventRow = Database["public"]["Tables"]["events"]["Row"]
type EventInsert = Database["public"]["Tables"]["events"]["Insert"]
type EventUpdate = Database["public"]["Tables"]["events"]["Update"]

export type EventTabFilter = "upcoming" | "past" | "drafts"

export interface UseEventsFilters {
  tab?: EventTabFilter
  search?: string
}

const PAGE_SIZE = 50

export function useEvents(orgId: string | null, filters?: UseEventsFilters) {
  const [events, setEvents] = useState<EventRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId) {
      setEvents([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const tab = filters?.tab ?? "upcoming"
      const search = filters?.search?.trim()

      let query = supabase
        .from("events")
        .select("*")
        .eq("org_id", orgId)
        .limit(PAGE_SIZE)

      const now = new Date().toISOString()

      if (tab === "upcoming") {
        query = query
          .eq("is_published", true)
          .gte("start_date", now)
          .order("start_date", { ascending: true })
      } else if (tab === "past") {
        query = query
          .eq("is_published", true)
          .lt("start_date", now)
          .order("start_date", { ascending: false })
      } else {
        query = query
          .eq("is_published", false)
          .order("updated_at", { ascending: false })
      }

      if (search) {
        query = query.or(`title.ilike.%${search}%,slug.ilike.%${search}%`)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        setError(fetchError.message)
        setEvents([])
        return
      }

      setEvents((data ?? []) as EventRow[])
    } finally {
      setLoading(false)
    }
  }, [orgId, filters?.tab, filters?.search])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { events, loading, error, refresh }
}

export function useEvent(orgId: string | null, eventId: string | null) {
  const [event, setEvent] = useState<EventRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId || !eventId || eventId === "new") {
      setEvent(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: fetchError } = await supabase
      .from("events")
      .select("*")
      .eq("id", eventId)
      .eq("org_id", orgId)
      .maybeSingle()

    if (fetchError) {
      setError(fetchError.message)
      setEvent(null)
    } else {
      setEvent((data ?? null) as EventRow | null)
    }
    setLoading(false)
  }, [orgId, eventId])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { event, loading, error, refresh }
}

export function useCreateEvent(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createEvent = useCallback(
    async (input: {
      title: string
      slug?: string
      description_sections?: Json
      start_date: string
      end_date?: string | null
      location_name?: string | null
      location_address?: string | null
      location_type?: "in_person" | "virtual" | "hybrid" | null
      virtual_url?: string | null
      cover_image?: string | null
      is_published?: boolean
      max_attendees?: number | null
      registration_enabled?: boolean
      waitlist_enabled?: boolean
      registration_deadline?: string | null
      capacity?: number | null
      created_by?: string | null
      seo_title?: string | null
      seo_description?: string | null
      og_image?: string | null
    }): Promise<EventRow | null> => {
      if (!orgId) return null
      setLoading(true)
      setError(null)

      const slug =
        input.slug?.trim() || generateSlug(input.title) || "untitled-event"

      const insert: EventInsert = {
        org_id: orgId,
        title: input.title.trim(),
        slug,
        description_sections: input.description_sections ?? ([] as Json),
        start_date: input.start_date,
        end_date: input.end_date ?? null,
        location_name: input.location_name ?? null,
        location_address: input.location_address ?? null,
        location_type: input.location_type ?? null,
        virtual_url: input.virtual_url ?? null,
        cover_image: input.cover_image ?? null,
        is_published: input.is_published ?? false,
        max_attendees: input.max_attendees ?? null,
        registration_enabled: input.registration_enabled ?? false,
        waitlist_enabled: input.waitlist_enabled ?? false,
        registration_deadline: input.registration_deadline ?? null,
        capacity: input.capacity ?? null,
        created_by: input.created_by ?? null,
        seo_title: input.seo_title?.trim() || null,
        seo_description: input.seo_description?.trim() || null,
        og_image: input.og_image?.trim() || null,
      }

      const { data, error: insertError } = await supabase
        .from("events")
        .insert(insert)
        .select()
        .single()

      setLoading(false)
      if (insertError) {
        setError(
          insertError.code === "23505"
            ? "An event with this URL already exists. Use a different slug."
            : insertError.message
        )
        return null
      }
      return data as EventRow
    },
    [orgId]
  )

  return { createEvent, loading, error }
}

export function useUpdateEvent(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateEvent = useCallback(
    async (id: string, updates: EventUpdate): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      const { data, error: updateError } = await supabase
        .from("events")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .eq("org_id", orgId)
        .select("slug")
        .maybeSingle()
      setLoading(false)
      if (updateError) {
        setError(
          updateError.code === "23505"
            ? "An event with this URL already exists. Use a different slug."
            : updateError.message
        )
        return false
      }
      const slug = (data as { slug: string } | null)?.slug
      if (slug) {
        queuePublicCachePurge(orgId, { type: "event-save", eventSlug: slug })
      }
      return true
    },
    [orgId]
  )

  return { updateEvent, loading, error }
}

export function useDeleteEvent(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deleteEvent = useCallback(
    async (id: string): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      const { data, error: deleteError } = await supabase
        .from("events")
        .delete()
        .eq("id", id)
        .eq("org_id", orgId)
        .select("slug")
        .maybeSingle()
      setLoading(false)
      if (deleteError) {
        setError(deleteError.message)
        return false
      }
      const slug = (data as { slug: string } | null)?.slug
      if (slug) {
        queuePublicCachePurge(orgId, { type: "event-delete", eventSlug: slug })
      }
      return true
    },
    [orgId]
  )

  return { deleteEvent, loading, error }
}

/** NOS-49: Fetch attendees for an event (contacts linked via event_attendees). */
export function useEventAttendees(orgId: string | null, eventId: string | null) {
  const [attendees, setAttendees] = useState<
    Array<{
      id: string
      event_id: string
      contact_id: string
      status: "registered" | "cancelled"
      guest_count: number
      created_at: string
      contacts: { id: string; email: string; name: string | null } | null
    }>
  >([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId || !eventId || eventId === "new") {
      setAttendees([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: fetchError } = await supabase
      .from("event_attendees")
      .select(
        `
        id,
        event_id,
        contact_id,
        status,
        guest_count,
        created_at,
        contacts (id, email, name)
      `
      )
      .eq("event_id", eventId)
      .in("status", ["registered", "cancelled"])
      .order("created_at", { ascending: false })

    if (fetchError) {
      setError(fetchError.message)
      setAttendees([])
    } else {
      setAttendees((data ?? []) as typeof attendees)
    }
    setLoading(false)
  }, [orgId, eventId])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { attendees, loading, error, refresh }
}
